import Draggable from "react-draggable";
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../../../../common/Button";
import CustomInput from "../../../../common/CustomInput";
import { Transition } from "@headlessui/react";
import MedicineSelect from "../../services/select/medication-select";
import DosageSelect from "../../services/select/dosage-select";
import FormSelect from "../../services/select/form-select";
import RouteSelect from "../../services/select/route-select";
import FrequencySelect from "../../services/select/frequency-select";
import PRNSelect from "../../services/select/prn-select";
import {
  isFieldError,
  isNameError,
  isValidVigilanceSelection,
} from "../../../../../helpers/input-validations";
import ConfirmationModalNew from "../../../../common/ConfirmationModalNew";
import CommonSelect from "../../services/select/common-creatable-select";
import Select from "react-select";
import { customReactSelectStyles } from "../../services/select/medication-select";
import { reasonOptions, PRN_FREQUENCY_VALUES } from "../clinical-assessment/data";
import MedicalConditionSelect from "../../services/select/medical-condition";
import {
  AdherenceTypes,
  OverrideReasonOptions,
  WhoOptions,
  WhenOptions,
  WhyOptions,
  CommonQuestionValueTypes,
} from "./data";

// Keep the dialog fully on-screen (matches interventionCreate.jsx sizing).
// Created HC-87 to refactor this and define a reusable component for pop-up forms.
const MODAL_MAX_SIZE = [1400, 1000];
const MODAL_MARGIN = 20;
const MODAL_VIEWPORT_BUFFER = 120;

const InterventionAssessment = ({
  open,
  onClose = () => {},
  onIntervention = () => {},
  onSave = () => {},
  selectedMedicationCard = null,
  selectedDrug = null,
  subType = null,
  informationStep = false,
}) => {
  const initialMedicine = {
    doseOptions: [],
    dose: null,
    formOptions: [],
    form: null,
    routeOptions: [],
    route: null,
    prn: false,
    prnDetail: null,
    frequency: null,
  };
  const [selectedMedicine, setSelectedMedicine] = useState({
    doseOptions: [],
    dose: null,
    formOptions: [],
    form: null,
    routeOptions: [],
    route: null,
  });
  const [selectedDose, setSelectedDose] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [selectedFrequency, setSelectedFrequency] = useState(null);
  const [selectedPRN, setSelectedPRN] = useState(null);
  const [prn, setPrn] = useState(false);

  const [reason, setReason] = useState(null);

  const [sig, setSig] = useState(null);
  const [whoChangedMedication, setWhoChangedMedication] = useState("patient");
  const [whyChangedMedication, setWhyChangedMedication] = useState(null);
  const [reasonComments, setReasonComments] = useState(null);
  const [whenChangedMedication, setWhenChangedMedication] = useState("");
  const [whenChangedMedicationDate, setWhenChangedMedicationDate] = useState(null);
  const [whenChangedDateOption, setWhenChangedDateOption] = useState(null);
  const [overrideReason, setOverrideReason] = useState(null);
  const [overrideOption, setOverrideOption] = useState(null);
  const [clickIntervention, setClickIntervention] = useState(null);

  const [comments, setComments] = useState(null);
  const [whoChangedMedicationError, setWhoChangedMedicationError] =
    useState(false);
  const [whyChangedMedicationError, setWhyChangedMedicationError] =
    useState(false);
  const [overrideReasonError, setOverrideReasonError] = useState(false);
  const [medicationNameInputMode, setMedicationNameInputMode] = useState(false);
  const [selectedMedicineError, setSelectedMedicineError] = useState(false);
  const [openUncodedDrug, setOpenUncodedDrug] = useState(false);
  const [selectedDoseError, setSelectedDoseError] = useState(false);
  const [selectedFormError, setSelectedFormError] = useState(false);
  const [selectedRouteError, setSelectedRouteError] = useState(false);
  const [selectedFrequencyError, setSelectedFrequencyError] = useState(false);
  const [selectedPRNError, setSelectedPRNError] = useState(false);
  const [selectedReasonError, setSelectedReasonError] = useState(false);
  const [submitCliked, setsubmitCliked] = useState(false);

  const vigilanceFieldsToCheck = [
    {
      field: selectedMedicine,
      checkFn: isValidVigilanceSelection,
      setErrorFn: setSelectedMedicineError,
      name: "selectedMedicine",
    },
    {
      field: selectedDose,
      checkFn: isValidVigilanceSelection,
      setErrorFn: setSelectedDoseError,
      name: "selectedDose",
    },
    {
      field: selectedForm,
      checkFn: isValidVigilanceSelection,
      setErrorFn: setSelectedFormError,
      name: "selectedForm",
    },
    {
      field: selectedRoute,
      checkFn: isValidVigilanceSelection,
      setErrorFn: setSelectedRouteError,
      name: "selectedRoute",
    },
    {
      field: selectedFrequency,
      checkFn: isValidVigilanceSelection,
      setErrorFn: setSelectedFrequencyError,
      name: "selectedFrequency",
    },
    {
      field: selectedPRN,
      checkFn: isValidVigilanceSelection,
      setErrorFn: setSelectedPRNError,
      name: "selectedPRN",
    },
    {
      field: reason,
      checkFn: isValidVigilanceSelection,
      setErrorFn: setSelectedReasonError,
      name: "reason",
    },
  ];

  const fieldsToCheck = [
    {
      field: whoChangedMedication,
      checkFn: isFieldError,
      setErrorFn: setWhoChangedMedicationError,
      alter: false,
    },
    {
      field: whyChangedMedication,
      checkFn: isFieldError,
      setErrorFn: setWhyChangedMedicationError,
      alter: false,
    },
  ];

  const isAsPrescribed = selectedMedicationCard?.consumption === AdherenceTypes.AsPrescribed;

  useEffect(() => {
    let interventionObj = selectedMedicationCard?.intervention;
    setWhenChangedMedication(
      interventionObj ? interventionObj.whenChangedMedication : null
    );
    setWhenChangedMedicationDate(
      interventionObj ? interventionObj.whenChangedDate : null
    );
    setWhenChangedDateOption(
      interventionObj ? interventionObj.whenChangedDateOption : null
    );
    setWhoChangedMedication(
      interventionObj ? interventionObj?.whoChangedMedication : "patient"
    );
    setWhyChangedMedication(
      interventionObj ? interventionObj?.whyChangedMedication : null
    );
    setReasonComments(
      interventionObj ? interventionObj?.otherChangeReason : null
    );
    setOverrideOption(interventionObj ? interventionObj?.override : null);
    setOverrideReason(interventionObj ? interventionObj?.overrideReason : null);
    setSelectedMedicine({
      displayText: interventionObj
        ? interventionObj?.medicationName
        : selectedDrug?.medicationName,
      label: interventionObj
        ? interventionObj?.medicationName
        : selectedDrug?.medicationName,
      identifier: interventionObj
        ? interventionObj?.medicationGenCode
        : selectedDrug?.medicationGenCode,
      value: interventionObj
        ? interventionObj?.medicationGenCode
        : selectedDrug?.medicationGenCode,
      type: interventionObj
        ? interventionObj?.medicationGenCodeType
        : selectedDrug?.medicationGenCodeType,
      dose: interventionObj ? interventionObj?.dosage : selectedDrug?.dosage ?? null,
    });
    setSelectedDose(
      interventionObj
        ? {
            label: interventionObj?.dosage,
            value: interventionObj?.dosage,
          }
        : selectedDrug?.dosage
        ? { label: selectedDrug.dosage, value: selectedDrug.dosage }
        : null
    );
    setSelectedForm(
      interventionObj
        ? {
            label: interventionObj?.form,
            value: interventionObj?.form,
          }
        : {
            label: selectedDrug?.form,
            value: selectedDrug?.form,
          }
    );
    setSelectedRoute(
      interventionObj
        ? {
            label: interventionObj?.route,
            value: interventionObj?.route,
          }
        : {
            label: selectedDrug?.route,
            value: selectedDrug?.route,
          }
    );
    setSelectedFrequency(
      interventionObj
        ? {
            label: interventionObj?.frequency,
            value: interventionObj?.frequency,
          }
        : null
    );
    setSelectedPRN(
      interventionObj
        ? {
            label:
              reasonOptions.find(
                (reason) => reason.value === interventionObj?.prnReason
              )?.label || interventionObj?.prnReason,
            value: interventionObj?.prnReason,
          }
        : null
    );
    setReason(
      interventionObj
        ? {
            label:
              reasonOptions.find(
                (reason) =>
                  reason.value === interventionObj?.reasonForMedication
              )?.label || interventionObj?.reason,
            value: interventionObj?.reason,
          }
        : {
            label:
              reasonOptions.find(
                (reason) =>
                  reason.value === selectedMedicationCard?.reasonForMedication
              )?.label || selectedMedicationCard?.reasonForMedication,
            value: selectedMedicationCard?.reasonForMedication,
          }
    );
    setSig(interventionObj ? interventionObj?.sig : null);
    setComments(interventionObj ? interventionObj?.comments : null);
    setsubmitCliked(false);
  }, [open, selectedMedicationCard]);

  const resetForm = () => {
    setsubmitCliked(false);
    setSelectedMedicine(null);
    setSelectedDose(null);
    setSelectedForm(null);
    setSelectedRoute(null);
    setSelectedFrequency(null);
    setSelectedPRN(null);
    setSig("");
    setComments("");
    setOverrideOption(null);
    setWhoChangedMedication(null);
    setWhyChangedMedication(null);
    setWhenChangedMedication(null);
    setWhenChangedMedicationDate(null);
    setWhenChangedDateOption(null);
    setOverrideReason(null);
  };

  const handleClick = (createIntervention) => {
    if (createIntervention !== null) {
      setClickIntervention(createIntervention);
    }

    manageStep(
      createIntervention !== null ? createIntervention : clickIntervention
    );
    setClickIntervention(null);
  };

  const manageStep = (createIntervention) => {
    setsubmitCliked(true);
    let allFieldsValid = true;
    if (!isAsPrescribed) {
      fieldsToCheck.forEach(({ field, checkFn, setErrorFn, alter }) => {
        const hasError = alter ? !checkFn(field) : checkFn(field);
        setErrorFn(hasError);
        if (hasError) {
          allFieldsValid = false;
        }
      });
    }

    if (selectedMedicationCard.consumption === AdherenceTypes.NotAsPrescribed) {
      let fieldsToValidate = vigilanceFieldsToCheck;
      if (prn) {
        fieldsToValidate = fieldsToValidate.filter(
          ({ name }) => name !== "reason"
        );
      }
      if (!prn) {
        fieldsToValidate = fieldsToValidate.filter(
          ({ name }) => name !== "selectedPRN"
        );
      }
      fieldsToValidate.forEach(({ field, checkFn, setErrorFn }) => {
        const hasError = !checkFn(field);
        setErrorFn(hasError);
        if (hasError) {
          allFieldsValid = false;
        }
      });
    }
    if (allFieldsValid) {
      let reviewedMedication = {
        medicationId: selectedMedicationCard.id,
        whenChangedMedication: whenChangedMedication,
        whenChangedDate: whenChangedMedicationDate,
        whenChangedDateOption: whenChangedDateOption,
        whoChangedMedication: whoChangedMedication,
        whyChangedMedication: whyChangedMedication,
        otherChangeReason: reasonComments,
        medicationName: selectedMedicine?.displayText,
        medicationGenCode: selectedMedicine?.identifier,
        medicationGenCodeType: selectedMedicine?.type,
        dosage: selectedDose?.value,
        form: selectedForm?.value,
        frequency: selectedFrequency?.value,
        route: selectedRoute?.value,
        prn: prn,
        prnReason: selectedPRN?.value,
        reason: reason?.value,
        comments: comments,
        sig: sig,
        override: overrideOption,
        overrideReason: overrideReason,
      };

      if (createIntervention) onIntervention(reviewedMedication);
      else onSave(reviewedMedication);

      resetForm();
    }
  };

  useEffect(() => {
    if (submitCliked && !isAsPrescribed) {
      fieldsToCheck.forEach(({ field, checkFn, setErrorFn, alter }) => {
        const hasError = alter ? !checkFn(field) : checkFn(field);
        setErrorFn(hasError);
      });
    }
  }, [...fieldsToCheck.map(({ field }) => field)]);

  useEffect(() => {
    if (!selectedMedicine) {
      setSelectedDose(null);
      setSelectedForm(null);
      setSelectedRoute(null);
    }
  }, [selectedMedicine]);

  useEffect(() => {
    if (subType && !informationStep) {
      setSelectedDose({
        label: "Use as Directed",
        value: "Use as Directed",
      });
      setSelectedForm({
        label: "Rinse",
        value: "Rinse",
      });
      setSelectedRoute({
        label: "Intranasal",
        value: "Intranasal",
      });
      setSelectedFrequency({
        label: "As Directed",
        value: "As Directed",
      });
    }
  }, [subType, informationStep]);

  const contentRef = useRef(null);
  const headerRef = useRef(null);
  const footerRef = useRef(null);
  const formBodyRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 792, height: 900 });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [container, setContainer] = useState(null);

  useEffect(() => {
    const el = document.getElementById("main-content-area");
    if (el) setContainer(el);
  }, []);

  // Measure the natural size of the dialog, then cap it so it always fits
  // within the viewport (header + scroll body + footer stay visible).
  useEffect(() => {
    if (!open || !contentRef.current) return;
    const el = contentRef.current;
    let mounted = true;

    const measure = () => {
      const originalWidth = el.style.width;
      el.style.width = "auto";
      const naturalWidth = el.offsetWidth;
      el.style.width = originalWidth;
      const naturalHeight = el.offsetHeight;

      const minHeight = Math.max(300, Math.floor(window.innerHeight * 0.25));
      const capWidth = Math.min(
        naturalWidth,
        MODAL_MAX_SIZE[0],
        window.innerWidth - MODAL_MARGIN * 2
      );
      const capHeight = Math.min(
        naturalHeight,
        MODAL_MAX_SIZE[1],
        Math.max(minHeight, window.innerHeight - MODAL_VIEWPORT_BUFFER)
      );
      if (!mounted) return;
      setDimensions({ width: capWidth, height: capHeight });

      // Pin the scroll body so the footer never spills past the dialog.      
      const headerH = headerRef.current ? headerRef.current.offsetHeight : 0;
      const footerH = footerRef.current ? footerRef.current.offsetHeight : 0;
      const calcMax = Math.max(120, capHeight - headerH - footerH - 24);
      if (formBodyRef.current) {
        formBodyRef.current.style.maxHeight = `${calcMax}px`;
      }
    };

    const raf = window.requestAnimationFrame(measure);
    return () => {
      mounted = false;
      window.cancelAnimationFrame(raf);
    };
  }, [open]);

  // Re-center / reset size the next time the dialog opens.
  useEffect(() => {
    if (!open) {
      setDimensions({ width: 792, height: 900 });
      setPosition({ x: 0, y: 0 });
    }
  }, [open]);

  const [showInterventionConfirmation, setShowInterventionConfirmation] =
    useState(false);
  const [keepIntervention, setKeepIntervention] = useState(false);
  const renderOverrideReasonLabel = (opt) => {
    if (!opt?.label) return null;
    if (opt.subLabel)
      return (
        <div>
          {opt.label}
          <br />
          <p className="italic">{opt.subLabel}</p>
        </div>
      );

    const [first, ...rest] = opt.label.split(";");
    const suffix = rest.join(";").trim();
    return suffix ? (
      <div>
        <span className="text-teal-300 font-semibold">{first.trim()}</span>
        <span>; {suffix}</span>
      </div>
    ) : (
      opt.label
    );
  };

  const handleContinue = (confirm, keepIntervention) => {
    if (
      confirm &&
      selectedMedicationCard?.intervention !== null &&
      selectedMedicationCard?.intervention?.urgencyLevel !== null &&
      selectedMedicationCard?.intervention?.urgencyLevel !== undefined
    )
      setShowInterventionConfirmation(true);
    else {
      handleClick(keepIntervention);
    }
  };

  if (!selectedMedicationCard) return null;

  if (!container) return null;

  console.log("Rendering Intervention Assessment with selectedMedicationCard: ", selectedMedicine);

  return createPortal(
    <>
      <Transition
        enter="transition-opacity duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        show={open}
      >
        <div className="absolute inset-0 flex items-center justify-center z-50 pointer-events-auto">
          <Draggable
            handle=".resize-header"
            bounds="parent"
            position={position}
            onDrag={(e, data) => setPosition({ x: data.x, y: data.y })}
          >
            <div
              style={{ width: dimensions.width, height: dimensions.height }}
              className="pointer-events-auto"
            >
              <div
                ref={contentRef}
                className="bg-white dialog pointer-events-auto flex flex-col h-full w-full max-h-[calc(100vh-120px)] overflow-hidden border-2 border-gray-100 rounded-md shadow-lg"
              >
                <div
                  className=""
                  onClick={() => onCross()}
                  aria-hidden="true"
                />
                <div className="flex flex-col flex-1 h-full min-h-0 overflow-hidden">
                  {/* header */}
                  <div
                    ref={headerRef}
                    className="relative resize-header shrink-0"
                  >
                    <div className="dialog-header flex items-start justify-between space-x-3 px-6 py-4 bg-gray-20 border-b border-gray-100">
                      <div className="flex flex-col space-x-1">
                        {selectedMedicationCard !== null &&
                          selectedMedicationCard.intervention &&
                          selectedMedicationCard.intervention
                            .interventionType !== null && (
                            <button
                              className="flex items-center"
                              onClick={() => {
                                handleContinue(false, true);
                              }}
                            >
                              <ChevronLeftIcon className="w-4 h-4 mb-2 text-teal-300" />
                              <h3 className="text-body-sm font-semibold text-teal-300 mb-2 hover:border-b-2 hover:border-teal-300">
                                Back to Intervention Results
                              </h3>
                            </button>
                          )}
                        <h2 className="font-semibold text-gray-900 text-display-xs">
                          Intervention Details - Assessment
                        </h2>
                      </div>
                      <div className="flex items-center h-7">
                        <button
                          type="button"
                          className="flex items-center justify-center p-1 transition-all rounded-full max-w-8 cursor-pointer right-4 top-3 bg-blue-200 hover:bg-REELTeal hover:text-white text-teal-300"
                          onClick={() => onClose(false)}
                        >
                          <span className="sr-only">Close panel</span>
                          <XMarkIcon className="w-5 h-5" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </div>

                    {/* body */}
                    <div
                      id="formBody"
                      ref={formBodyRef}
                      className="flex flex-col flex-1 min-h-0 overflow-y-auto"
                    >
                      {selectedMedicationCard !== null && (
                        <div className={`px-6 pb-6 ${isAsPrescribed ? "pt-6" : ""}`}>
                          <div className="flex flex-col gap-5">
                            <div className="flex flex-col">
                              <div className="text-gray-800 flex flex-row text-xl px-4 py-2 my-3 bg-blue-100 border-2 border-blue-400 rounded-md">
                                <span className="font-semibold text-body-lg text-REELTeal flex items-start">
                                  Adherence Status -{" "}
                                  {isAsPrescribed
                                    ? "Taking Medication as Prescribed"
                                    : selectedMedicationCard.consumption === AdherenceTypes.Stopped
                                    ? "Stopped Taking Medication"
                                    : "Not Taking Medication as Prescribed"}
                                </span>
                              </div>
                              <div className="flex-row text-gray-800 flex pt-3">
                                <span className="text-base font-medium items-start flex min-w-[330px]">
                                  Medication Name
                                </span>
                                <p className="text-base font-semibold items-start flex justify-start">
                                  {selectedMedicationCard?.name}
                                </p>
                              </div>
                            </div>

                            {!isAsPrescribed && <div className="border-t-[1px] border-gray-100 mx-6"></div>}
                            {!isAsPrescribed && (<div className="flex flex-col gap-2 pt-4">
                              <div className="flex flex-row space-x-5">
                                <p className="min-w-[310px] text-gray-800 flex items-center">
                                  <span className="text-base font-normal">
                                    <b>WHEN</b>
                                    {selectedMedicationCard.consumption ===
                                    AdherenceTypes.Stopped
                                      ? " was the medication stopped"
                                      : " did the change occur"}
                                    ?
                                  </span>
                                </p>
                                <CustomInput
                                  name="whenChangedMedication"
                                  type="date"
                                  className="col-span-12 w-full"
                                  label=""
                                  placeholder="Select Date"
                                  value={whenChangedMedicationDate}
                                  dateOptionValue={whenChangedDateOption}
                                  showDateOptions={true}
                                  isIntervention={true}
                                  onDateOptionChange={(dateVal, dateOptionVal) => {
                                    const newDateVal = dateVal || null;
                                    const newDateOptionVal = dateOptionVal || null;
                                    
                                    setWhenChangedMedicationDate(newDateVal);
                                    setWhenChangedDateOption(newDateOptionVal);
                                    
                                    // Update display value based on selection type
                                    const TEXT_OPTIONS = ["Date Not Known", "Date Not Specified", "Less than 3 months", "3 to 6 months", "6 months to 1 year", "Greater than one year"];
                                    const CALENDAR_OPTIONS = ["(YYYY)", "(MM/YYYY)", "(DD/MM/YYYY)"];
                                    const OPTION_LABELS = {
                                      "(YYYY)": "Select Date - Year only",
                                      "(MM/YYYY)": "Select Date - Month, Year", 
                                      "(DD/MM/YYYY)": "Select Date - Specific Date"
                                    };
                                    
                                    let displayValue = "";
                                    if (dateOptionVal && TEXT_OPTIONS.includes(dateOptionVal)) {
                                      displayValue = dateOptionVal;
                                    } else if (dateVal) {
                                      displayValue = dateVal;
                                    } else if (dateOptionVal && CALENDAR_OPTIONS.includes(dateOptionVal)) {
                                      displayValue = OPTION_LABELS[dateOptionVal] || "";
                                    }
                                    
                                    setWhenChangedMedication(displayValue);
                                  }}
                                  showOptionalLabel={false}
                                />
                              </div>
                              <div className="flex flex-row space-x-5">
                                <p className="min-w-[310px] text-gray-800 flex items-center">
                                  <span className="text-base font-normal">
                                    <b>WHO</b>
                                    {""}
                                    {selectedMedicationCard.consumption ===
                                    AdherenceTypes.Stopped
                                      ? " stopped the medication"
                                      : " initiated the change"}
                                    {""}? *
                                  </span>
                                </p>
                                <CustomInput
                                  options={WhoOptions}
                                  name="severity"
                                  className={`col-span-12 w-full ${
                                    whoChangedMedication === null
                                      ? "!bg-blue-100"
                                      : "bg-white"
                                  }`}
                                  label=""
                                  type="dropdown"
                                  value={whoChangedMedication}
                                  onChange={(e) => {
                                    setWhoChangedMedication(e.value);
                                  }}
                                  error={whoChangedMedicationError}
                                />
                              </div>
                              <div className="flex flex-row space-x-5">
                                <p className="min-w-[310px] text-gray-800 flex items-center">
                                  <span className="text-base font-normal">
                                    <b>WHY</b>
                                    {selectedMedicationCard.consumption ===
                                    AdherenceTypes.Stopped
                                      ? " was the medication stopped"
                                      : " did the change occur"}
                                    ? *
                                  </span>
                                </p>
                                <CustomInput
                                  options={WhyOptions}
                                  name="severity"
                                  className={`col-span-12 w-full ${
                                    whyChangedMedication === null
                                      ? "!bg-blue-100"
                                      : "bg-white"
                                  }`}
                                  label=""
                                  type="dropdown"
                                  value={whyChangedMedication}
                                  onChange={(e) => {
                                    setWhyChangedMedication(e.value);
                                  }}
                                  error={whyChangedMedicationError}
                                  initialOptions={
                                    selectedMedicationCard.consumption ===
                                    AdherenceTypes.Stopped
                                      ? "stopped"
                                      : "changed"
                                  }
                                />
                              </div>
                              {whyChangedMedication !== null && (
                                <div className="flex justify-end space-x-5">
                                  <p className="min-w-[310px] text-gray-800 flex items-center"></p>
                                  <CustomInput
                                    name="other reason"
                                    className="col-span-12 w-full bg-white"
                                    label=""
                                    placeholder={
                                      whyChangedMedication ===
                                      "Other (Free Text)"
                                        ? "Add Reason"
                                        : "Comments"
                                    }
                                    inputClassName="!placeholder-gray-600"
                                    type="text"
                                    value={reasonComments}
                                    onChange={(e) => {
                                      setReasonComments(e.target.value);
                                    }}
                                  />
                                </div>
                              )}

                              {selectedMedicationCard.safe ===
                                CommonQuestionValueTypes.Yes &&
                                selectedMedicationCard.working ===
                                  CommonQuestionValueTypes.No && (
                                  <div className="text-teal-950 pt-4">
                                    Medication may not be effective, potential
                                    side effects may impact adherence. May
                                    reassess the treatment plan and consider to
                                    create an intervention.
                                  </div>
                                )}
                              {selectedMedicationCard.safe ===
                                CommonQuestionValueTypes.Yes &&
                                selectedMedicationCard.working !==
                                  CommonQuestionValueTypes.No && (
                                  <div className="text-teal-950 pt-4">
                                    Possible side effects may affect medication
                                    adherence. May reassess the treatment plan
                                    and consider to create an intervention.
                                  </div>
                                )}
                              {selectedMedicationCard.safe !==
                                CommonQuestionValueTypes.Yes &&
                                selectedMedicationCard.working ===
                                  CommonQuestionValueTypes.No && (
                                  <div className="text-teal-950 pt-4">
                                    Medication may not be effective. May
                                    reassess the treatment plan and consider to
                                    create an intervention.
                                  </div>
                                )}
                            </div>)}
                          </div>
                        </div>
                      )}
                      {!isAsPrescribed && selectedMedicationCard.consumption ===
                        AdherenceTypes.NotAsPrescribed && (
                        <>
                          <div className="border-t-[1px] border-gray-100 mx-6"></div>
                          <div className="px-6 py-6">
                            <div className="grid grid-cols-12 gap-3">
                              <span className="col-span-12 font-semibold text-lg pb-2">
                                How are you taking the medication?
                              </span>
                              <MedicineSelect
                                onChange={(medicine) => {
                                  setSelectedMedicine(medicine);
                                }}
                                isRequired={true}
                                onNewCreate={(value) =>
                                  setMedicationNameInputMode(value)
                                }
                                error={selectedMedicineError}
                                value={selectedMedicine}
                              />
                              {medicationNameInputMode && (
                                <p className="col-span-12 text-body-sm text-red-200 font-medium">
                                  This is an UNCODED DRUG - No Clinical Check
                                  will be performed
                                </p>
                              )}
                              <DosageSelect
                                selectedMedicine={
                                  selectedMedicine || initialMedicine
                                }
                                onChange={(dose) => {
                                  setSelectedDose(dose);
                                }}
                                isRequired={true}
                                error={selectedDoseError}
                                value={selectedDose}
                                subType={subType}
                              />
                              <FormSelect
                                selectedMedicine={
                                  selectedMedicine || initialMedicine
                                }
                                onChange={(form) => {
                                  setSelectedForm(form);
                                }}
                                isRequired={true}
                                error={selectedFormError}
                                value={selectedForm}
                              />
                              <RouteSelect
                                selectedMedicine={
                                  selectedMedicine || initialMedicine
                                }
                                onChange={(route) => {
                                  setSelectedRoute(route);
                                }}
                                isRequired={true}
                                error={selectedRouteError}
                                value={selectedRoute}
                              />
                              <FrequencySelect
                                onChange={(frequency) => {
                                  setSelectedFrequency(frequency);
                                  if (PRN_FREQUENCY_VALUES.includes(frequency?.value)) {
                                    setPrn(true);
                                  }
                                }}
                                isRequired={true}
                                subType={subType}
                                error={selectedFrequencyError}
                                value={selectedFrequency}
                                selectedMedicine={
                                  selectedMedicine || initialMedicine
                                }
                              />
                              <Button
                                type={"primary-outline"}
                                text="Customize"
                                className="col-span-4 !px-2.5 !py-3.5"
                                disabled={true}
                              />
                              <CustomInput
                                name="sig"
                                className="col-span-12 bg-white"
                                label="SIG"
                                placeholder="SIG"
                                value={sig}
                                onChange={(e) => {
                                  setSig(e.target.value);
                                }}
                                type="text"
                              />
                              <div className="p-1 col-span-12"></div>
                              <Button
                                type={
                                  selectedMedicine?.prn || prn
                                    ? "primary"
                                    : "primary-outline"
                                }
                                text="PRN"
                                onClick={() => {
                                  setPrn(!prn);
                                }}
                                className="col-span-4 !px-2.5 !py-3.5"
                              />
                              <PRNSelect
                                isDisabled={!prn}
                                selectedMedicine={
                                  selectedMedicine || initialMedicine
                                }
                                onChange={(prn) => {
                                  setSelectedPRN(prn);
                                }}
                                isRequired={true}
                                error={selectedPRNError}
                                value={selectedPRN}
                              />
                              {
                                <MedicalConditionSelect
                                  value={reason}
                                  onChange={(condition) => {
                                    setReason(condition);
                                  }}
                                  defaultOptions={reasonOptions}
                                  error={selectedReasonError}
                                  label="Reason"
                                  tyoe="reason"
                                  isCreatable={true}
                                  isRequired={true}
                                />
                              }
                            </div>
                          </div>
                        </>
                      )}

                      <div className="flex flex-col px-6 py-6 gap-12 bg-gray-20">
                        <div className="flex flex-col col-span-12 text-base font-medium gap-2">
                          <div className="font-semibold text-[18px] text-teal-300">
                            Pharmacist Clinical Decision
                          </div>
                          {
                            (() => {
                              const optionsList =
                                selectedMedicationCard.consumption ===
                                AdherenceTypes.Stopped
                                  ? OverrideReasonOptions.filter(
                                      (x) => x.value !== "modify" && x.value !== "modify-withoutNotify"
                                    )
                                  : OverrideReasonOptions.filter(
                                      (x) => x.value !== "stopped" && x.value !== "stopped-withoutNotify"
                                    );
                              const selectedObj = optionsList.find((o) => o.value === overrideOption) || null;
                              return (
                                <div className={`col-span-12 w-full ${ overrideOption === null
                                    ? "bg-yellow-50 border-yellow-300" : "bg-white" } px-3 py-3 rounded-[4px] border-[1px]`}>
                                  <Select
                                    isClearable
                                    styles={customReactSelectStyles(true, selectedObj)}
                                    isDisabled={false}
                                    onChange={(opt) => {
                                      setOverrideOption(opt ? opt.value : null);
                                    }}
                                    options={optionsList}
                                    value={selectedObj}
                                    placeholder={"Select Reason to Continue"}
                                    // This control sits at the very bottom of the
                                    // scrollable form. Portal the menu to <body> and
                                    // position it fixed so it floats above the dialog
                                    // (never clipped by #formBody's overflow) and
                                    // "auto" placement flips it upward when there's no
                                    // room below — no auto-scroll / form growth needed.
                                    menuPlacement="auto"
                                    menuPosition="fixed"
                                    menuShouldScrollIntoView={false}
                                    menuPortalTarget={
                                      typeof document !== "undefined"
                                        ? document.body
                                        : undefined
                                    }
                                    closeMenuOnScroll={(e) =>
                                      e.target === formBodyRef.current
                                    }
                                    formatOptionLabel={(opt) =>
                                      renderOverrideReasonLabel(opt)
                                    }
                                  />
                                </div>
                              );
                            })()
                          }
                          {/* Show Explanation only when user selects "Other; Add Explanation" */}
                          {overrideOption === "free" && (
                            <>
                              <CustomInput
                                name="Explanation"
                                className="col-span-12 bg-white"
                                label="Explanation"
                                inputClassName="!placeholder-gray-600"
                                placeholder={"Add Explanation"}
                                showInputCancellation={!!overrideReason}
                                showOptionalLabel={false}
                                value={overrideReason}
                                onChange={(e) => {
                                  setOverrideReason(e.target.value);
                                }}
                                onClose={() => {
                                  // Clear the explanation text when X is clicked
                                  setOverrideReason("");
                                }}
                                type="text"
                              />
                            </>
                          )}
                          <p className="font-normal pl-1 text-sm text-teal-600">
                            {overrideOption === "stopped-withoutNotify" ? (
                              <span className="font-semibold">
                                No intervention required — Pharmacist Confirmed
                                Provider Notification Not Required
                              </span>
                            ) : (
                              <span>
                                A Clinical Communication Form will be available
                                in Documentation for Physician and Pharmacist.
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>

                  {/* footer */}
                  <div
                    ref={footerRef}
                    className="flex items-center justify-center shrink-0 py-6 px-6 bg-white border-t border-gray-100"
                  >
                    <div className="flex items-center justify-center space-x-5">
                      <Button
                        className="min-w-[200px]"
                        text="Cancel"
                        type="primary-outline"
                        onClick={() => {
                          onClose(false);
                          resetForm();
                        }}
                      />
                      <Button
                        className="min-w-[200px]"
                        disabled={
                          overrideOption === "modify-withoutNotify" ||
                          overrideOption === "stopped-withoutNotify" ||
                          overrideOption === null ||
                          (!isAsPrescribed && whoChangedMedication === null) ||
                          (!isAsPrescribed && whyChangedMedication === null)
                        }
                        text={
                          selectedMedicationCard?.intervention?.urgencyLevel !==
                            null &&
                          selectedMedicationCard?.intervention?.urgencyLevel !==
                            undefined
                            ? "Edit Intervention"
                            : "Create Intervention"
                        }
                        type="primary"
                        onClick={() => {
                          handleContinue(false, true);
                        }}
                      />
                      <Button
                        className="min-w-[200px]"
                        text={
                          selectedMedicationCard?.intervention !== null
                            ? "Update & Continue"
                            : "Continue Review"
                        }
                        type="primary"
                        disabled={
                          overrideOption === "reassess" ||
                          overrideOption === "modify" ||
                          overrideOption === null ||
                          (!isAsPrescribed && whoChangedMedication === null) ||
                          (!isAsPrescribed && whyChangedMedication === null)
                        }
                        onClick={() => {
                          if (medicationNameInputMode) {
                            setOpenUncodedDrug(true);
                          } else {
                            handleContinue(true, false);
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Draggable>
        </div>
      </Transition>
      <ConfirmationModalNew
        open={openUncodedDrug}
        title={"Uncoded Drug Alert"}
        type={"primary"}
        confirmText={"Continue"}
        cancelText="Cancel & Review"
        content={() => (
          <div className="flex flex-col  text-gray-700">
            <p className="text-body-sm">
              An uncoded DRUG has been added.
              <br />
              Profile Analysis can not be performed.
            </p>
            <p className="text-body-sm mt-4">
              A manual clinical check will be required.
            </p>
          </div>
        )}
        onCross={() => {
          setOpenUncodedDrug(false);
        }}
        handleClose={() => {
          setOpenUncodedDrug(false);
        }}
        confirmAction={() => {
          handleContinue(true, false);
          setOpenUncodedDrug(false);
        }}
      />
      <ConfirmationModalNew
        open={showInterventionConfirmation}
        title={"Confirm Intervention Status"}
        type={"primary-outline"}
        confirmText={"Yes"}
        cancelText="No"
        cancelBtnType="primary-outline"
        className="!w-[435px]"
        content={() => (
          <p>Do you wish to continue with the new Intervention Status?</p>
        )}
        onCross={() => {
          setShowInterventionConfirmation(false);
        }}
        handleClose={() => {
          setShowInterventionConfirmation(false);
          setKeepIntervention(false);
          handleContinue(false, false);
        }}
        confirmAction={() => {
          setShowInterventionConfirmation(false);
          setKeepIntervention(true);
          handleContinue(false, true);
        }}
      />
    </>,
    container
  );
};

export default InterventionAssessment;
